import { useInfiniteQuery } from '@tanstack/react-query';
import { Activity } from 'lucide-react';
import { useParams } from 'react-router';
import { api, useProject } from '../../api/hooks';
import type { AuditEvent, Page } from '../../api/types';
import { useCrumbs } from '../../layout/crumbs';
import { EmptyState, QueryView } from '../../ui/feedback';
import { LoadMore, PageHeader, usePageTitle } from '../../ui/misc';
import { EventTable } from '../shared/EventTable';

/** AUD-01–03 project business history. */
export function ActivityPage() {
  const { key } = useParams();
  const project = useProject(key);
  usePageTitle(project.data ? `${project.data.name} · Activity` : null);
  useCrumbs([{ label: project.data?.name ?? key ?? '', to: `/projects/${key}` }, { label: 'Activity' }]);
  const q = useInfiniteQuery({
    queryKey: ['activity', key],
    queryFn: ({ pageParam }) =>
      api.get<Page<AuditEvent>>(`/projects/${key}/activity${pageParam ? `?cursor=${pageParam}` : ''}`),
    initialPageParam: '',
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  return (
    <div className="page">
      <PageHeader
        title="Activity"
        subtitle="Business events with actor, source, human authority and server time. Times show in your timezone; hover for UTC."
      />
      <QueryView query={{ ...q, data: q.data }}>
        {(data) => {
          const items = data.pages.flatMap((p) => p.items);
          if (!items.length) return <EmptyState icon={Activity} title="No activity yet" />;
          return (
            <>
              <EventTable events={items} projectKey={key!} />
              <LoadMore
                hasMore={!!q.hasNextPage}
                onClick={() => q.fetchNextPage()}
                loading={q.isFetchingNextPage}
              />
            </>
          );
        }}
      </QueryView>
    </div>
  );
}
